<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, computed, watch } from 'vue'
import { Carousel, Navigation, Slide } from 'vue3-carousel'

import type { ImpactColor, ImpactType, IProject } from '@/api/interfaces'
import { debounce, isElementInViewport } from '@/helpers/utils'
import type { ICarousel, IMapPin } from '@/helpers/interfaces'
import { useEditorStore } from '@/stores/Editor'
import { usePublicData } from '@/stores/PublicData'

import PublicMapIcon from '@/components/icons/PublicMapIcon.vue'
import PublicMapPin from '@/components/public-page/PublicMapPin.vue'
import PublicProjectCard from '@/components/public-page/PublicProjectCard.vue'

interface Props {
  projects: IProject[]
  colorSettings: Record<ImpactType, ImpactColor>
}

const props = defineProps<Props>()

const editorStore = useEditorStore()
const publicDataStore = usePublicData()

const accountSignupDate = new Date(publicDataStore.signupDate).getTime()
const projectsToDisplay = computed(() =>
  props.projects
    .filter(
      ({ link, type }) =>
        link &&
        (Object.keys(props.colorSettings) as Array<ImpactType>).some(
          (colorSettingImpactType) => type === colorSettingImpactType,
        ),
    )
    .filter(
      ({ type }) =>
        // only display projects that are set to be displayed
        editorStore.editor.icon.impactDisplay[type],
    )
    .filter(({ projectId, type, status, statusInitiatedAt }) =>
      // display all active project pins and cards if there is no impact data
      // otherwise only display projects with impacts
      // do not display deprecated carbon projects where it was deprecated before the account's start date
      publicDataStore.impacts?.length
        ? publicDataStore.impacts?.some(
            (i) =>
              ('projectId' in i && i.projectId === projectId) ||
              (i.type === 'carbon' &&
                i.amount > 0 &&
                type === 'carbon' &&
                (status === 'deprecated'
                  ? accountSignupDate < new Date(statusInitiatedAt).getTime()
                  : true)),
          )
        : status === 'approved',
    ),
)

const breakPoints = {
  0: { itemsToShow: 1 },
  600: { itemsToShow: projectsToDisplay.value.length >= 2 ? 2 : 1 },
  960: { itemsToShow: projectsToDisplay.value.length >= 2 ? 3 : 1 },
}
const carousel = ref<ICarousel | null>(null)
const currentSlide = ref(0)

const mapPins: IMapPin[] = projectsToDisplay.value.flatMap((project, index) => {
  return project.countries
    ? project.countries
        .filter((country) => country !== '')
        .map((country) => ({
          index,
          country,
          type: project.type,
          projectId: project.projectId,
          availability: project.availability,
          link: project.link,
        }))
    : []
})

const setPin = (pin: IMapPin) => {
  const mapCell = document.getElementById(
    `public-map-${pin.country
      .toLowerCase()
      .replace(/\s*-\s*/g, '-')
      .replace(' ', '-')}-${pin.type}`,
  ) as HTMLElement
  const SVGRect = mapCell?.getBoundingClientRect() || new DOMRect()
  const pinIcon = document.getElementById(
    `public-map-pin-${pin.country.replace(/\s*-\s*/g, '-')}-${pin.type}`,
  ) as HTMLElement
  const pinIconHeight = pinIcon?.getBoundingClientRect().height || 0
  const pinIconWidth = pinIcon?.getBoundingClientRect().width || 0
  const cellHeight = SVGRect.height
  const cellWidth = SVGRect.width
  const mapCellPosition = {
    top: SVGRect.top + window.scrollY,
    left: SVGRect.left + window.scrollX,
  }

  if (mapCell && pinIcon) {
    pinIcon.style.top = `${mapCellPosition.top - pinIconHeight + cellHeight}px`
    pinIcon.style.left = `${mapCellPosition.left - pinIconWidth / 2 + cellWidth / 2}px`
  }
}

const initMap = () => {
  mapPins.forEach((pin: IMapPin) => {
    setPin(pin)
  })
}

const handlePinClick = (index: number) => {
  const mapEl = document.getElementById('public-map')
  const carouselEl = document.getElementById('public-map-carousel')

  if (!isElementInViewport(carouselEl)) {
    mapEl?.scrollIntoView()
  }

  carousel.value?.slideTo(index)
}

const handleResize = debounce(initMap, 100)

watch(
  () => editorStore.editor.icon.impactDisplay,
  () => {
    handleResize()
  },
  { deep: true },
)

onMounted(() => {
  handleResize()
  window.addEventListener('resize', handleResize)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize)
})
</script>

<template>
  <section>
    <v-container>
      <div class="map-container">
        <PublicMapPin
          class="map-pin"
          v-for="(pin, index) in mapPins"
          :key="index"
          :pin="pin"
          :is-active="currentSlide === pin.index"
          @click="() => handlePinClick(pin.index)"
        />
        <PublicMapIcon id="public-map" />
        <Carousel
          v-if="projectsToDisplay.length"
          id="public-map-carousel"
          ref="carousel"
          v-model="currentSlide"
          :wrap-around="true"
          :breakpoints="breakPoints"
        >
          <Slide v-for="(project, index) in projectsToDisplay" :key="project.projectId">
            <PublicProjectCard
              :project="project"
              :color-settings="colorSettings"
              :active="currentSlide === index"
              :impact="
                publicDataStore.impacts?.find(
                  (i) =>
                    ('projectId' in i && i.projectId === project.projectId) ||
                    (i.type === 'carbon' && i.amount > 0 && project.type === 'carbon'),
                )
              "
            />
          </Slide>
          <template #addons>
            <Navigation />
          </template>
        </Carousel>
      </div>
    </v-container>
  </section>
</template>

<style lang="scss">
.map-container {
  width: 100%;
  margin-bottom: 20px;

  @include media('md-and-up') {
    margin-bottom: 80px;
  }
}

.carousel__slide {
  padding: 30px;
  transition: transform 100ms ease-in-out;

  @include media('md-and-up') {
    &--active {
      transform: scale(1.2);
    }
  }
}

.carousel__prev,
.carousel__next {
  box-sizing: content-box;
  z-index: 2;
}

.carousel__track {
  z-index: 1;
}

.carousel__icon {
  width: 24px;
  height: 24px;

  .light & {
    fill: var(--darkest);
  }

  .dark & {
    fill: var(--lightest);
  }

  .community-page & {
    fill: var(--ui-emerald-green);
  }
}

#public-map {
  .community-page & {
    > path {
      fill: rgba(241, 241, 241, 0.5);
    }
  }
}
</style>
